import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';

import PageHeader from '../../components/PageHeader.jsx';
import Loader, { ErrorBox } from '../../components/Loader.jsx';
import { SuperAdminAPI } from '../../lib/api';
import { notify } from '../../lib/notifications';
import { ModuleToggleList } from './ModuleDefaults.jsx';


/**
 * Super Admin → Organisation modules.
 *
 * Organisation-level activation (not per-user).  Users inside the
 * organisation can only be granted modules that are on here.
 */
export default function OrganisationModules() {
  const { id } = useParams();
  const [payload, setPayload] = useState(null);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await SuperAdminAPI.organisationModules.get(id);
      setPayload(data);
      setModules(data.modules || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { reload(); }, [reload]);

  const toggle = (key) => {
    setModules((current) => current.map((item) => (
      item.key === key ? { ...item, is_active: !item.is_active } : item
    )));
  };

  const save = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const keys = modules.filter((item) => item.is_active).map((item) => item.key);
      const data = await SuperAdminAPI.organisationModules.save(id, keys);
      setPayload(data);
      setModules(data.modules || []);
      notify.success('Organisation modules updated.');
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  }, [id, modules]);

  if (!id) return <Navigate to="/super-admin/organisations" replace />;
  if (loading && !payload) return <Loader label="Loading organisation modules…" />;

  const org = payload && payload.organisation;
  const dashboard = payload && payload.dashboard;
  const title = org ? `${org.name || org.sub_domain || 'Organisation'} modules` : 'Organisation modules';

  return (
    <>
      <PageHeader
        title={`🧩 ${title}`}
        subtitle="Organisation-level control. Users only see modules that are activated here; organisation admins cannot turn extra modules on."
        actions={
          <>
            <Link className="ty-btn ty-btn--ghost" to="/super-admin/organisations">
              ← Organisations
            </Link>
            {dashboard && dashboard.available && dashboard.path && (
              <Link className="ty-btn ty-btn--ghost" to={dashboard.path}>
                Open business dashboard
              </Link>
            )}
            <button type="button" className="ty-btn ty-btn--ghost" onClick={reload} disabled={loading || saving}>
              ↻ Refresh
            </button>
            <button type="button" className="ty-btn" onClick={save} disabled={saving || loading}>
              {saving ? 'Saving…' : 'Save modules'}
            </button>
          </>
        }
      />
      <ErrorBox error={error} />
      {org && org.is_public && (
        <div className="ty-card" style={{ marginBottom: 16 }}>
          This is the <strong>public organisation</strong>. Only super-admin
          administers it and its users. Open the business dashboard to manage
          people and structure on this host.
        </div>
      )}
      <ModuleToggleList modules={modules} onToggle={toggle} activeKey="is_active" />
    </>
  );
}
